import axios, { AxiosError } from 'axios';
import { getSupabase, supabaseEnabled } from './supabase';

// Em produção (Netlify) a API é servida na mesma origem, em /api.
const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
export const API = `${BASE}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('pp_admin_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Painel admin direto no Supabase: as rotas /admin/*, /products e /bulk-tiers rodam aqui no navegador
 * (mesmo código das regras da API) e falam com o banco usando o login do admin. Pagamento, webhook e
 * as rotas públicas continuam indo para a API.
 */
const defaultAdapter = axios.getAdapter(api.defaults.adapter);
let localReady;
async function loadLocal() {
  localReady ||= (async () => {
    const [{ handleLocal, isLocalRoute }, rest] = await Promise.all([
      import('../../netlify/lib/router.client.mjs'),
      import('../../netlify/lib/db.rest.mjs'),
    ]);
    rest.setClient(await getSupabase());
    return { handleLocal, isLocalRoute };
  })();
  return localReady;
}

function unauthorized(config) {
  const response = { status: 401, statusText: 'Unauthorized', data: { detail: 'Sessão expirada' }, headers: {}, config };
  return new AxiosError('Sessão expirada', AxiosError.ERR_BAD_REQUEST, config, null, response);
}

if (supabaseEnabled) {
  api.defaults.adapter = async (config) => {
    const path = `/${String(config.url || '').replace(/^\/+/, '')}`;
    // Só o painel admin (logado) usa o Supabase direto. O site público nunca baixa esse código (é pesado).
    const isAdminRoute = path === '/admin' || path.startsWith('/admin/');
    if (!isAdminRoute || !localStorage.getItem('pp_admin_token')) return defaultAdapter(config);
    const { handleLocal, isLocalRoute } = await loadLocal();
    if (!isLocalRoute(path)) return defaultAdapter(config);

    const supabase = await getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw unauthorized(config);

    const query = new URLSearchParams(config.params || {}).toString();
    const body = config.data == null ? undefined : (typeof config.data === 'string' ? config.data : JSON.stringify(config.data));
    const res = await handleLocal(new Request(`https://local/api${path}${query ? `?${query}` : ''}`, {
      method: String(config.method || 'get').toUpperCase(),
      headers: { 'content-type': 'application/json' },
      body: ['GET', 'HEAD'].includes(String(config.method || 'get').toUpperCase()) ? undefined : body,
    }));
    const data = await res.json();
    const response = { status: res.status, statusText: String(res.status), data, headers: {}, config };
    if (res.status >= 400) throw new AxiosError(data?.detail || 'Erro', AxiosError.ERR_BAD_REQUEST, config, null, response);
    return response;
  };
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('pp_admin_token');
      if (supabaseEnabled) getSupabase().then((sb) => sb.auth.signOut());
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(err);
  }
);
